/**
 * Text utility functions
 */

/**
 * Safely truncate text with proper null/undefined handling
 * @param text - The text to truncate (can be undefined/null)
 * @param maxLength - Maximum length before truncation
 * @param suffix - Suffix to add when text is truncated (default: "...")
 * @returns Truncated text or empty string if input is invalid
 */
export function truncateText(
  text: string | undefined | null,
  maxLength: number = 100,
  suffix: string = "..."
): string {
  // Handle null, undefined, or non-string values
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Return original text if it's within the limit
  if (text.length <= maxLength) {
    return text;
  }

  // Truncate and add suffix
  return text.substring(0, maxLength).trim() + suffix;
}

/**
 * Truncate text at word boundaries to avoid cutting words in half
 * @param text - The text to truncate (can be undefined/null)
 * @param maxLength - Maximum length before truncation
 * @param suffix - Suffix to add when text is truncated (default: "...")
 * @returns Truncated text at word boundary or empty string if input is invalid
 */
export function truncateTextAtWord(
  text: string | undefined | null,
  maxLength: number = 100,
  suffix: string = "..."
): string {
  // Handle null, undefined, or non-string values
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Return original text if it's within the limit
  if (text.length <= maxLength) {
    return text;
  }

  // Find the last space within the limit
  const truncated = text.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  // If no space found, use character truncation
  if (lastSpaceIndex === -1) {
    return truncated.trim() + suffix;
  }

  // Truncate at the last word boundary
  return text.substring(0, lastSpaceIndex).trim() + suffix;
}

/**
 * Strip HTML tags from text and then truncate
 * @param html - HTML string to process (can be undefined/null)
 * @param maxLength - Maximum length before truncation
 * @param suffix - Suffix to add when text is truncated (default: "...")
 * @returns Plain text truncated or empty string if input is invalid
 */
export function truncateHtml(
  html: string | undefined | null,
  maxLength: number = 100,
  suffix: string = "..."
): string {
  // Handle null, undefined, or non-string values
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Strip HTML tags (basic implementation)
  const plainText = html.replace(/<[^>]*>/g, '').trim();
  
  // Truncate the plain text
  return truncateText(plainText, maxLength, suffix);
}

/**
 * Validate and sanitize text input
 * @param text - Text to validate (can be undefined/null)
 * @param fallback - Fallback text if input is invalid
 * @returns Valid string or fallback
 */
export function safeText(
  text: string | undefined | null,
  fallback: string = ''
): string {
  if (!text || typeof text !== 'string') {
    return fallback;
  }
  return text.trim();
}
